import { Filter, Search, Map } from "lucide-react"
import { useTranslation } from 'react-i18next';

interface BranchItem {
  id: string
  name: string
  subtitle?: string
  badge?: "VIP" | "NEW"
}

import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { useAuth } from "@/components/auth-provider"
import { setSelectedBranch } from "@/lib/auth"
import { useState } from "react";
import { useBookingStore, savePendingBooking } from "@/store/booking-store";


export default function BranchGrid({ branches }: { branches: BranchItem[] }) {
  const router = useRouter()
  const [searchParams] = useSearchParamsCompat()
  const { isAuthenticated, isLoading } = useAuth()
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("")
  const { setBookingState, bookingType } = useBookingStore()

  const filteredBranches = branches.filter(branch =>
    branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (branch.subtitle && branch.subtitle.toLowerCase().includes(searchQuery.toLowerCase()))
  )


  // read locale from pathname since this file is client; lightweight extract


  function handleSelectBranch(branch: BranchItem) {
    if (isLoading) return

    const mode = searchParams?.get("mode")
    const appointmentId = searchParams?.get("appointment_id")

    // Set data in Zustand store
    setBookingState({
      selectedBranch: branch,
      selectedService: null, // Clear service
      selectedSlot: null, // Clear slot
      selectedDate: null, // Clear date
      mode: (mode as "booking" | "reschedule") || "booking",
      appointmentId: appointmentId,
      bookingStep: "service",
    })

    if (!isAuthenticated) {
      savePendingBooking({
        selectedBranch: branch,
        mode: (mode as "booking" | "reschedule") || "booking",
        appointmentId: appointmentId,
        bookingType: bookingType
      })

      const targetUrl = window.location.pathname + window.location.search
      const returnUrl = encodeURIComponent(targetUrl)
      router.push(`/auth/verify?returnUrl=${returnUrl}`)
      return
    }

    setSelectedBranch(branch.id)
  }


  return (
    <>
      <style>{`
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.5s ease-out forwards;
      }
    `}</style>

      <div className="px-2 md:px-4 w-full max-w-7xl mx-auto animate-fade-in-up">

        <div className="pt-4 pb-10 md:pb-8">


          <div className="rounded-3xl bg-white shadow-sm border border-gray-100 px-2 md:px-5 py-6 relative overflow-hidden">
            <div className="absolute top-10 -left-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
            <div className="absolute top-55 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />

            {/* Section Header */}
            <div className="hidden md:flex justify-between items-center mb-6 relative z-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">{t("selectBranch")}</h2>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {filteredBranches.length} {t("locations")}
              </span>
            </div>

            {/* Search Bar */}
            <div className="mb-8 flex items-center gap-3 relative z-10">
              <div className="relative flex-1 group">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`${t("searchBranch")} ...`}
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 border border-transparent focus:bg-white focus:border-pink-200 focus:outline-none focus:ring-4 focus:ring-pink-50/50 transition-all duration-300 shadow-sm group-hover:shadow-md"
                />
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-pink-500 transition-colors duration-300" />

                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                onClick={() => router.push("/map")}
                className="h-12 pl-2 pr-5 flex items-center gap-3 bg-white border border-gray-100 rounded-full shadow-sm hover:shadow-md hover:border-pink-100 transition-all duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center text-pink-600 group-hover:bg-pink-600 group-hover:text-white transition-all duration-300">
                  <Map size={16} />
                </div>
                <span className="font-semibold text-md text-gray-700 group-hover:text-pink-600 transition-colors">{t("Map")}</span>
              </button>
            </div>

            {/* Branch Grid */}
            {filteredBranches.length > 0 ? (
              <div className="grid grid-cols-3 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-6 relative z-10">
                {filteredBranches.map((branch, index) => (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => handleSelectBranch(branch)}
                    style={{ animationDelay: `${index * 50}ms` }}
                    className="group flex flex-col items-center gap-3 p-2 md:p-4 rounded-2xl border border-gray-200 bg-pink-50/10 shadow-md shadow-gray-900/10 hover:shadow-2xl hover:border-pink-200 hover:shadow-pink-200/50 hover:-translate-y-1 transition-all duration-300 relative animate-fade-in-up opacity-0 fill-mode-forwards"
                  >
                    {/* Badge */}
                    {branch.badge && (
                      <div
                        className={`absolute -top-2 -right-2 px-2.5 py-1 rounded-full text-[10px] md:text-xs font-bold text-white shadow-sm transform group-hover:scale-110 transition-transform duration-300 z-20 ${branch.badge === "VIP" ? "bg-gradient-to-r from-yellow-400 to-orange-500" : "bg-gradient-to-r from-pink-500 to-rose-500"
                          }`}
                      >
                        {branch.badge}
                      </div>
                    )}

                    {/* Branch Icon Container */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-pink-100 rounded-full blur-lg opacity-0 group-hover:opacity-70 transition-opacity duration-300" />
                      <img
                        src="/icon/branch.svg"
                        alt="branch"
                        width={34}
                        height={46}
                        className="w-[34px] h-[46px] md:w-[48px] md:h-[64px] relative z-10 transform group-hover:scale-110 transition-transform duration-300 ease-out"
                      />
                    </div>

                    {/* Branch Name */}
                    <p className="text-md md:text-md font-semibold md:text-sm text-gray-700 group-hover:text-pink-600 text-center leading-tight transition-colors duration-300 line-clamp-2 w-full">
                      {t(branch.name)}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in-up relative z-10">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 shadow-inner">
                  <Search className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t("No branches found")}</h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">
                  {t("We couldn't find any branches matching")} <span className="font-semibold text-gray-900">"{searchQuery}"</span>.
                </p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-6 py-2.5 bg-pink-600 text-white rounded-full text-sm font-bold hover:bg-gray-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 active:scale-95"
                >
                  {t("Clear Search")}
                </button>
              </div>
            )}


          </div>
        </div>
      </div>


    </>
  )
}
